import { Alert, Box, Button, Card, Center, Group, PasswordInput, SimpleGrid, Stack, Text, TextInput, ThemeIcon, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDocumentTitle } from '@mantine/hooks';
import { IconAlertCircle, IconChecklist, IconDatabase, IconFileCertificate } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { errorMessage } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { FullPageLoader } from '@/auth/guards';
import { Logo } from '@/layout/Logo';
import { homeFor, INTERNAL, safeNext } from '@/lib/paths';

const HIGHLIGHTS = [
  { icon: IconFileCertificate, title: 'Document requests', text: 'Raise, assign, upload and verify dossier documents in one place.' },
  { icon: IconChecklist, title: 'My tasks', text: 'Every department sees exactly what is due and when.' },
  { icon: IconDatabase, title: 'Data Bank', text: 'Certificates, registrations and dossiers, searchable and secure.' },
];

/** Staff sign-in (/internal/login). Clients sign in at /login. */
export function LoginPage() {
  useDocumentTitle('Staff sign-in · MExP');
  const { status, user, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v.trim()) ? null : 'Enter your work email'),
      password: (v) => (v ? null : 'Enter your password'),
    },
  });
  const [submitting, setSubmitting] = useState(false);
  const next = safeNext(params.get('next'), INTERNAL);

  if (status === 'loading') return <FullPageLoader />;
  if (status === 'authenticated') return <Navigate to={user?.userType === 'exporter' ? homeFor(user) : next} replace />;

  const submit = form.onSubmit(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      const signedIn = await login(values.email.trim(), values.password);
      navigate(signedIn.userType === 'exporter' ? homeFor(signedIn) : next, { replace: true });
    } catch (e) {
      setError(errorMessage(e));
      form.setFieldValue('password', '');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0} mih="100dvh">
      <Box
        visibleFrom="md"
        p={48}
        style={{
          background: 'linear-gradient(145deg, var(--mantine-color-brand-9) 0%, var(--mantine-color-brand-7) 55%, var(--mantine-color-cyan-7) 100%)',
          color: 'white',
        }}
      >
        <Stack justify="space-between" h="100%">
          <Group gap="sm">
            <ThemeIcon size={40} radius="md" color="white" variant="white" c="brand.8">
              <svg viewBox="0 0 64 64" width="24" height="24" aria-hidden>
                <path d="M16 46V18h6l10 14 10-14h6v28h-7V30l-9 12-9-12v16z" fill="currentColor" />
              </svg>
            </ThemeIcon>
            <Text fw={700} fz="lg">
              MExP
            </Text>
          </Group>
          <Stack gap="xl" maw={460}>
            <Title order={1} fz={38} lh={1.15}>
              Merchant Exporter Intelligence Portal
            </Title>
            <Text fz="lg" opacity={0.85}>
              Regulatory documents for every export market, prepared and verified in one system.
            </Text>
            <Stack gap="md">
              {HIGHLIGHTS.map((h) => (
                <Group key={h.title} gap="md" wrap="nowrap" align="flex-start">
                  <ThemeIcon size={36} radius="md" variant="white" c="brand.8">
                    <h.icon size={20} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text fw={600}>{h.title}</Text>
                    <Text fz="sm" opacity={0.8}>
                      {h.text}
                    </Text>
                  </Stack>
                </Group>
              ))}
            </Stack>
          </Stack>
          <Text fz="xs" opacity={0.7}>
            © Makin Laboratories Pvt. Ltd. · Pithampur, Indore
          </Text>
        </Stack>
      </Box>

      <Center p="lg">
        <Stack w="100%" maw={400} gap="lg">
          <Box hiddenFrom="md">
            <Logo />
          </Box>
          <Stack gap={4}>
            <Title order={2}>Sign in</Title>
            <Text c="dimmed" fz="sm">
              Use your Makin Laboratories account.
            </Text>
          </Stack>
          <Card component="form" onSubmit={submit} noValidate shadow="sm">
            <Stack gap="md">
              {error && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
                  {error}
                </Alert>
              )}
              <TextInput label="Email" placeholder="name@makinlaboratories.com" autoComplete="username" autoFocus type="email" {...form.getInputProps('email')} />
              <PasswordInput label="Password" autoComplete="current-password" {...form.getInputProps('password')} />
              <Button type="submit" loading={submitting} fullWidth size="md">
                Sign in
              </Button>
            </Stack>
          </Card>
          <Text c="dimmed" fz="xs" ta="center">
            Forgot your password? Ask an administrator to reset it.
          </Text>
          <Text c="dimmed" fz="xs" ta="center">
            Exporter or client?{' '}
            <Text component={Link} to="/login" fz="xs" c="brand" fw={600} inherit>
              Use the client portal
            </Text>
          </Text>
        </Stack>
      </Center>
    </SimpleGrid>
  );
}
